import React from 'react';
import { Badge, Box, Popover, PopoverTrigger, PopoverContent, BadgeProps, Text } from 'pounceV2';
import { extractRiskTags, riskTagLabel } from 'Pages/Alerts/ListAlerts/utils';
import RiskScoreEvidencePopoverV3 from './RiskScoreEvidencePopoverV3';

interface RiskScoreBadgeProps {
  contextTags: string[];
  alertId?: string;
}

const CLASSIFICATION_VARIANT_MAP: {
  [key: string]: BadgeProps['variant'];
} = {
  Benign: 'success-outline',
  Inconclusive: 'warning-outline',
  Risky: 'error-outline',
};

const RiskScoreBadge = ({ contextTags, alertId }: RiskScoreBadgeProps) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const riskTags = extractRiskTags(contextTags);
  const classificationLabel = riskTags.length > 0 ? riskTagLabel(riskTags[0]) : null;

  if (!classificationLabel) {
    return null;
  }

  const variant =
    CLASSIFICATION_VARIANT_MAP[classificationLabel] || CLASSIFICATION_VARIANT_MAP.Inconclusive;

  const badgeElement = (
    <Badge
      variant={variant}
      data-testid="risk-classification-badge"
      sx={{ textTransform: 'none' }}
      emphasized
    >
      <Text variant="body/sm" fontWeight="semibold">
        {classificationLabel}
      </Text>
    </Badge>
  );

  // If no alertId, render badge without popover
  if (!alertId) {
    return badgeElement;
  }

  // With alertId, render badge with interactive popover
  return (
    <Popover isOpen={isOpen} onChange={setIsOpen}>
      <PopoverTrigger
        as={Box}
        data-dd-privacy="allow"
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
      >
        {badgeElement}
      </PopoverTrigger>
      <PopoverContent>
        <Box onMouseEnter={() => setIsOpen(true)} onMouseLeave={() => setIsOpen(false)}>
          <RiskScoreEvidencePopoverV3 alertId={alertId} isOpen={isOpen} />
        </Box>
      </PopoverContent>
    </Popover>
  );
};

export default RiskScoreBadge;
